import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { X } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { msg } from "@lingui/core/macro";
import { cn } from "../lib/cn";
import { useI18n } from "../lib/i18n";

/**
 * Drawer — side-mounted Radix Dialog with `side` variant (right/left/bottom/top).
 *
 * Decision: we DO NOT depend on `vaul` — the brand needs only Action/Source/
 * Studio drawers, all of which fit a Radix Dialog rendered as a panel.
 * `vaul` would add ~7kB just for the drag
 * gesture which we don't ship in v2.0-alpha (a future PR can swap the
 * primitive without touching consumer code thanks to the same `Drawer.*`
 * API surface).
 */

const Drawer = DialogPrimitive.Root;
const DrawerTrigger = DialogPrimitive.Trigger;
const DrawerPortal = DialogPrimitive.Portal;
const DrawerClose = DialogPrimitive.Close;

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-ink-1/40 backdrop-blur-[2px] data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in",
      className
    )}
    {...props}
  />
));
DrawerOverlay.displayName = DialogPrimitive.Overlay.displayName;

// Slide keyframes live in the shared preset (`drawer-in-*` / `drawer-out-*`).
// Radix keeps the panel mounted while the exit animation runs, so the close
// slides too. (The previous `animate-in slide-in-from-right` names belonged
// to tailwindcss-animate, which was never installed — the panel snapped.)
const drawerVariants = cva(
  "fixed z-50 flex flex-col gap-4 border-hairline bg-card text-card-foreground shadow-xl",
  {
    variants: {
      side: {
        right:
          "inset-y-0 right-0 h-full w-3/4 border-l data-[state=closed]:animate-drawer-out-right data-[state=open]:animate-drawer-in-right sm:max-w-md",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:animate-drawer-out-left data-[state=open]:animate-drawer-in-left sm:max-w-md",
        top: "inset-x-0 top-0 w-full border-b data-[state=closed]:animate-drawer-out-top data-[state=open]:animate-drawer-in-top",
        bottom:
          "inset-x-0 bottom-0 w-full border-t data-[state=closed]:animate-drawer-out-bottom data-[state=open]:animate-drawer-in-bottom",
      },
    },
    defaultVariants: {
      side: "right",
    },
  }
);

export interface DrawerContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof drawerVariants> {
  /** Hide the built-in close button (`X` in the corner). Default `false`. */
  hideCloseButton?: boolean;
  /**
   * a11y fallback title. Radix Dialog primitive **requires** a Title
   * inside Content; consumers that don't render a visible ``<DrawerTitle>``
   * (or render it conditionally) MUST provide this so we can mount a
   * sr-only one — otherwise Radix logs ``DialogTitle must be used within
   * Dialog`` to the dev console every open/close. Default ``"Pannello"``.
   */
  ariaTitle?: string;
}

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DrawerContentProps
>(({ side = "right", className, children, hideCloseButton, ariaTitle, ...props }, ref) => {
  const { i18n } = useI18n();
  return (
    <DrawerPortal>
      <DrawerOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(drawerVariants({ side }), className)}
        {...props}
      >
        {/* Always-on sr-only Title — see comment in ``dialog.tsx``. Radix
            accepts multiple Title nodes (first wins as
            ``aria-labelledby``), and the always-on fallback prevents the
            warning when a consumer's visible ``<DrawerTitle>`` is
            conditionally rendered or unmounted during the close
            animation. */}
        <VisuallyHidden.Root>
          <DialogPrimitive.Title>
            {ariaTitle ?? i18n._(msg`Pannello`)}
          </DialogPrimitive.Title>
        </VisuallyHidden.Root>
        {children}
        {!hideCloseButton ? (
          <DialogPrimitive.Close className="absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none disabled:pointer-events-none">
            <X className="h-4 w-4" />
            <span className="sr-only">{i18n._(msg`Chiudi`)}</span>
          </DialogPrimitive.Close>
        ) : null}
      </DialogPrimitive.Content>
    </DrawerPortal>
  );
});
DrawerContent.displayName = "DrawerContent";

const DrawerHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col gap-1.5 border-hairline border-b px-5 pt-5 pb-3",
      className
    )}
    {...props}
  />
);
DrawerHeader.displayName = "DrawerHeader";

const DrawerBody = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex-1 overflow-y-auto px-5 py-4", className)} {...props} />
);
DrawerBody.displayName = "DrawerBody";

const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse gap-2 border-hairline border-t px-5 py-4 sm:flex-row sm:justify-end",
      className
    )}
    {...props}
  />
);
DrawerFooter.displayName = "DrawerFooter";

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "font-display font-semibold text-base text-ink-1 leading-none tracking-tight",
      className
    )}
    {...props}
  />
));
DrawerTitle.displayName = DialogPrimitive.Title.displayName;

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-ink-2 text-sm", className)}
    {...props}
  />
));
DrawerDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
  drawerVariants,
};
